import store from "@/store/BookingStore";
import { StatusV2, Traveller } from "../../../../../../requests-module/src";
import { QuoteUpdate } from "@/service/CatalogApiService/moelService";
import getCookie from "@/utils/getCookie";
import { isCSE } from "@/utils/cse";
import type { Locale } from "@/service/translate/flights/flightTranslations.types";

export class OrchestratorService {

    /**
     * Initiates a payment request with the provided user information using the Fetch API.
     * @param userInfo - The user information of type UserInfo.
     * @param locale - The locale for the request (default 'en_US').
     * @param purpose - The purpose of the payment (default 'FIRST_PAYMENT').
     */


    private getBaseUrl() {
        return process.env.VUE_APP_ORCHESTRATOR_V2_BASE_URL || window.env.VUE_APP_ORCHESTRATOR_V2_BASE_URL
    }

    public async initPayment(email: string, locale: string, purpose = 'FIRST_PAYMENT', forceFull = false): Promise<PaymentApiResponse | PaymentApiResponseStatus> {
        if (!store.getters.getToken) throw new Error('No booking in state')

        // Construct the URL with query parameters
        const url = new URL(`${this.getBaseUrl()}/${store.getters.getToken}/init-payment`);
        url.searchParams.append('locale', locale);
        url.searchParams.append('purpose', purpose);
        const body: { email: string; forceFull: boolean; company?: string; companyLang?: string } = { email, forceFull };
        const isCSEContext = isCSE(window.locale as Locale);
        if (store.getters.getSingleCompanyConfiguration?.CompanyName && isCSEContext) body.company = store.getters.getSingleCompanyConfiguration?.CompanyName;
        const companyLang = isCSEContext ? store.getters.getSingleCompanyConfiguration?.language : undefined;
        if (companyLang) body.companyLang = companyLang;

        try {
            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json() as PaymentApiResponse | PaymentApiResponseStatus;
            return data;
        } catch (error) {
            console.error('Error initiating payment:', error);
            throw error;
        }
    }

    /**
     * Makes a request to the /save-quote endpoint.
     * @returns A promise that resolves to the response of the API call.
     */
    public async saveQuote(quoteUpdate: QuoteUpdate): Promise<Response> {
        if (!store.getters.getToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${store.getters.getToken}/save-quote`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(quoteUpdate)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return response;
        } catch (error) {
            console.error('Error saving quote:', error);
            throw error;
        }
    }

    /**
     * Makes a GET call to the /get-status endpoint.
     * @returns A promise that resolves to an object containing the status.
     */
    public async getStatus(): Promise<{ status: StatusV2; eurostarReference?: string }> {
        if (!store.getters.getToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${store.getters.getToken}/get-status`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json() as { status: StatusV2; eurostarReference?: string };
            return data;
        } catch (error) {
            console.error('Error getting status:', error);
            throw error;
        }
    }

    /**
     * Makes a POST call to the /check-payment endpoint.
     * 
     * @returns A promise that resolves to a boolean.
     */

    public async checkPayment(forceFull = false): Promise<boolean> {
        if (!store.getters.getToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${store.getters.getToken}/check-payment`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    payments: store.getters.getState.initPaymentResponse.payments,
                    forceFull
                })
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json() as { Ok: boolean };
            return data.Ok;
        } catch (error) {
            console.error('Error checking payment:', error);
            throw error;
        }
    }

    /**
     * Makes a PUT call to the /set-status-processing endpoint with no body.
     * The response is expected to be an object with a boolean status.
     * @returns A promise that resolves to an object containing the status.
     */
    public async setStatusProcessing(): Promise<{ status: boolean }> {
        if (window.location.hostname === 'localhost') {
            return { status: true };
        }
        if (!store.getters.getToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${store.getters.getToken}/set-status-processing`;

        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json() as { status: boolean };
            return data;
        } catch (error) {
            console.error('Error setting status processing:', error);
            throw error;
        }
    }

    /**
     * Updates the list of participants for the current booking.
     * @param participants - An array of participant objects to be sent in the request body.
     * @returns A promise that resolves when the request is successful.
     */
    public async updateParticipants(participants: Traveller[]): Promise<{ status: StatusV2 }> {
        if (!store.getters.getToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${store.getters.getToken}/participants`;

        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json, text/plain, */*'
                },
                body: JSON.stringify(participants)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json() as { status: StatusV2 };
            return data
        } catch (error) {
            console.error('Error updating participants:', error);
            throw error;
        }
    }

    /**
     * Makes a GET call to the /availability endpoint to check accommodation and Eurostar availability.
     * @param token - The booking token (optional, uses store token if not provided).
     * @returns A promise that resolves to the availability response.
     */
    public async getAvailability(): Promise<AvailabilitiesApiResponse> {
        const bookingToken = store.getters.getToken;
        if (!bookingToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${bookingToken}/availability`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json() as AvailabilitiesApiResponse;

            return data;
        } catch (error) {
            console.error('Error getting availability:', error.message);
            throw error;
        }
    }
    public async getBalanceStatus(boReference: string, paymentIds: string[] = []): Promise<{ status: StatusV2 }> {
        const params = new URLSearchParams({ type: "balance" });
        if (paymentIds.length > 0) {
            params.set("paymentIds", paymentIds.join(","));
        }
        const url = `${this.getBaseUrl()}/${encodeURIComponent(boReference)}/get-status?${params.toString()}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json() as { status: StatusV2 };
            return data;
        } catch (error) {
            console.error('Error getting balance status:', error);
            throw error;
        }
    }

    public async notify(): Promise<{ status: boolean }> {
        if (!store.getters.getToken) throw new Error('No booking in state');

        const url = `${this.getBaseUrl()}/${store.getters.getToken}/notify` // ?target=all;
        const body: { company?: string } = {};
        if (store.getters.getSingleCompanyConfiguration?.CompanyName && isCSE(window.locale as Locale)) body.company = store.getters.getSingleCompanyConfiguration?.CompanyName;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json() as { status: boolean };
            return data;
        } catch (error) {
            console.error('Error notifying:', error);
            throw error;
        }
    }

}

export interface PaymentApiResponse {
    paymentModes: string[];
    boReference: string;
    payments: Payment[];
    paymentOptions: PaymentOption[];
    paymentCondition: number;
    cancelationDate: string;
    totalPrice: number;
}

export interface PaymentApiResponseStatus {
    status: string;
}

// Availability response types
export interface AccommodationsResponse {
    liheId: number;
    prestId: number;
    startDate: string;
    endDate: string;
    availability: {
        available: boolean;
        currentSellingPrice: number;
    };
}

export interface EurostarAvailabilityResponse {
    available: boolean;
    matchedTransportId?: string;
    error?: string;
}

export interface TneAvailabilityResponse {
    available: boolean;
}

export interface AvailabilitiesApiResponse {
    accommodations: AccommodationsResponse[];
    eurostar?: EurostarAvailabilityResponse;
    tne?: TneAvailabilityResponse;
}

// Represents each individual payment.
export interface Payment {
    status: string;
    amount: number;
    date: string;
    orderId: number;
    paymentId: string;
    currency: string;
}

// Represents each payment option.
export interface PaymentOption {
    data: PaymentData | PaymentDataSystempay | PaymentDataAdyen;
    method: string;
    key: string;
}
// Detailed information about the payment option (Stripe in this case).
export interface PaymentData {
    id: string;
    object: string;
    amount: number;
    amount_capturable: number;
    amount_details: AmountDetails;
    amount_received: number;
    application: any;
    application_fee_amount: any;
    automatic_payment_methods: any;
    canceled_at: any;
    cancellation_reason: any;
    capture_method: string;
    client_secret: string;
    confirmation_method: string;
    created: number;
    currency: string;
    customer: string;
    description: string | null
    excluded_payment_method_types: string[];
    invoice: string | null;
    last_payment_error: any;
    latest_charge: any;
    livemode: boolean;
    metadata: Metadata;
    next_action: any;
    on_behalf_of: any;
    payment_method: any;
    payment_method_configuration_details: any;
    payment_method_options: PaymentMethodOptions;
    payment_method_types: string[];
    processing: any;
    receipt_email: any;
    review: any;
    setup_future_usage: string;
    shipping: any;
    source: any;
    statement_descriptor: any;
    statement_descriptor_suffix: any;
    status: string;
    transfer_data: any;
    transfer_group: any;
}
export interface PaymentDataSystempay {
    formToken?: string;
    paymentId?: string;
    status?: string;
}
export interface PaymentDataAdyen {
    id: string;
    sessionData: string;
    clientKey: string;
}

// Additional details for the amount.
export interface AmountDetails {
    tip: Record<string, unknown>;
}

// Metadata attached to the payment intent.
export interface Metadata {
    boReference: string;
    bookingRef: string;
    cardHolderName: string;
    company: string;
    email: string;
    locale: string;
    order_id: string;
    payments: string;
    purpose: string;
    stayEndDate: string;
    stayStartDate: string;
    totalPrice: string;
    user_id: string;
}

// Payment method options specific to Stripe.
export interface PaymentMethodOptions {
    card?: CardPaymentMethodOptions;
    ideal?: any;
    bancontact?: any;
}

// Card-specific payment method options.
export interface CardPaymentMethodOptions {
    installments: any;
    mandate_options: any;
    network: any;
    request_three_d_secure: string;
}

enum PaymentStatus {
    INIT = "INIT",
    PROCESSING = "PROCESSING",
    OVERBOOKING = "OVERBOOKING",
    ERROR = "ERROR",
    QUOTE_SAVED = "QUOTE_SAVED",
    QUOTE_FAILED = "QUOTE_FAILED",
    PAID = "PAID",
    FAILED = "FAILED"
}
